import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { cn } from '@/lib/utils';
import { crosswordUiCell } from '@/utils/crosswordUtils';

interface CrosswordGridProps {
  gridData: crosswordUiCell[][];
  onCellChange: (row: number, col: number, value: string) => void;
  activeCell: { row: number; col: number } | null;
  setActiveCell: (cell: { row: number; col: number } | null) => void;
  setActiveClue: (number: number) => void;
  direction: 'across' | 'down';
  setDirection: (direction: 'across' | 'down') => void;
  isComplete: boolean;
}

export interface CrosswordGridRef {
  handleKeyDown: (e: React.KeyboardEvent, row: number, col: number) => void;
}

const CrosswordGrid = forwardRef<CrosswordGridRef, CrosswordGridProps>(({
  gridData,
  onCellChange,
  activeCell,
  setActiveCell,
  setActiveClue,
  direction,
  setDirection,
  isComplete
}, ref) => {
  const gridRefs = useRef<(HTMLInputElement | null)[][]>(
    Array(gridData.length)
      .fill(null)
      .map(() => Array(gridData[0].length).fill(null))
  );
  const touchStartTime = useRef<number>(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isScrolling = useRef(false);

  const getCurrentWordCells = (row: number, col: number) => {
    const cells: { row: number; col: number }[] = [];
    if (direction === 'across') {
      // Find start of word
      let startCol = col;
      while (startCol > 0 && gridData[row][startCol - 1].answer != '') {
        startCol--;
      }
      // Add all cells in the word
      let currentCol = startCol;
      while (currentCol < gridData[0].length && gridData[row][currentCol].answer != '') {
        cells.push({ row, col: currentCol });
        currentCol++;
      }
    } else {
      // Find start of word
      let startRow = row;
      while (startRow > 0 && gridData[startRow - 1][col].answer != '') {
        startRow--;
      }
      // Add all cells in the word
      let currentRow = startRow;
      while (currentRow < gridData.length && gridData[currentRow][col].answer != '') {
        cells.push({ row: currentRow, col });
        currentRow++;
      }
    }
    return cells;
  };

  const handleCellInteraction = (e: React.MouseEvent | React.TouchEvent, row: number, col: number) => {
    e.stopPropagation();
    if (gridData[row][col].answer != '') {
      handleCellClick(e as React.MouseEvent, row, col);
    }
  };

  const handleCellClick = (e: React.MouseEvent, row: number, col: number) => {
    // If clicking the active cell, toggle direction
    if (activeCell && activeCell.row === row && activeCell.col === col) {
      changeDirection(row, col);
    } else {
      setActiveCell({ row, col });
      setActiveClueFromCell(row, col);
    }
  };

  const changeDirection = (row: number, col: number) => {
    if (gridData[row][col].answer != '') {
      if(gridData[row][col].isDown && direction === 'across') {
        setDirection('down');
        setActiveClue(gridData[row][col].downClue);
      }
      else if(gridData[row][col].isAcross && direction === 'down') {
        setDirection('across');
        setActiveClue(gridData[row][col].acrossClue);
      }
    }
  }

  const handleRightClick = (e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault(); // Prevent context menu
    changeDirection(row, col);
  };

  const handleKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
    if (gridData[row][col].answer == '') return;

    switch (e.key) {
      case 'ArrowRight':
        moveNext(row, col, 0, 1);
        break;
      case 'ArrowLeft':
        moveNext(row, col, 0, -1);
        break;
      case 'ArrowDown':
        moveNext(row, col, 1, 0);
        break;
      case 'ArrowUp':
        moveNext(row, col, -1, 0);
        break;
      case 'Tab':
        e.preventDefault();
        moveToNextWord();
        break;
      case 'Backspace':
        if (gridData[row][col].currValue && !gridData[row][col].isRevealed) {
          onCellChange(row, col, '');
        } else {
          // Move to previous cell if current cell is empty
          const previousCell = movePrevious(row, col);
          if (gridData[previousCell.row][previousCell.col].currValue) {
            onCellChange(previousCell.row, previousCell.col, '');
          }
        }
        break;
      default:
        if (e.key.length === 1 && e.key.match(/[a-zA-Z]/)) {
          onCellChange(row, col, e.key.toUpperCase());
          // Move to next cell after the current cell is updated
          setTimeout(() => {
            moveToNextCell(row, col);
          }, 2);
        }
        if(e.key.length == 1 && e.key.match(/[0-9@#$%&*\-+()!?,.;:/]/)) {
          onCellChange(row, col, e.key);
          // Move to next cell after the current cell is updated
          setTimeout(() => {
            moveToNextCell(row, col);
          }, 2);
        }
        break;
    }
  };

  const moveNext = (row: number, col: number, rowOffset: number, colOffset: number) => {
    let newRow = row + rowOffset;
    let newCol = col + colOffset;

    // Keep moving until we find a non-empty cell or hit the edge
    while (
      newRow >= 0 &&
      newRow < gridData.length &&
      newCol >= 0 &&
      newCol < gridData[0].length
    ) {
      if (gridData[newRow][newCol].answer != '') {
        setActiveCell({ row: newRow, col: newCol });
        setActiveClueFromCell(newRow, newCol);
        return;
      }
      newRow += rowOffset;
      newCol += colOffset;
    }
  };

  const movePrevious = (row: number, col: number) => {
    const colOffset = direction === 'across' ? -1 : 0;
    const rowOffset = direction === 'down' ? -1 : 0;
    
    let newRow = row + rowOffset;
    let newCol = col + colOffset;

    if (
      newRow >= 0 &&
      newRow < gridData.length &&
      newCol >= 0 &&
      newCol < gridData[0].length &&
      gridData[newRow][newCol].answer != ''
    ) {
      setActiveCell({ row: newRow, col: newCol });
      return { row: newRow, col: newCol };
    }
    else {
      return moveToPreviousWordWithRowCol(row, col, direction);
    }
  };

  const moveToPreviousWordWithRowCol = (row: number, col: number, direction: 'across' | 'down', iteration = 0) => {
    if(direction === 'across') {
      let currNum = gridData[row][col].acrossClue;
      let currCol = col;
      let bestNum = -1;
      let bestRow = -1;
      let bestCol = -1;
      for(let i = row; i >= 0; i--) {
        for(let j = currCol; j >= 0; j--) {
          if(gridData[i][j].acrossClue < currNum && gridData[i][j].acrossClue > bestNum && gridData[i][j].answer != '' && gridData[i][j].isAcross) {
            bestNum = gridData[i][j].acrossClue;
            bestRow = i;
            bestCol = j;
          }
        }
        currCol = gridData[0].length - 1;
      }
      if(bestNum != -1) {
        setActiveCell({ row: bestRow, col: bestCol });
        return { row: bestRow, col: bestCol };
      }
      else {
        for(let i = gridData.length - 1; i >= 0; i--) {
          for(let j = gridData[0].length - 1; j >= 0; j--) {
            if(gridData[i][j].downClue != 0 && gridData[i][j].answer != '' && gridData[i][j].downClue > bestNum) {
              bestNum = gridData[i][j].downClue;
              bestRow = i;
              bestCol = j;
            }
          }
        }
        setActiveCell({ row: bestRow, col: bestCol });
        setDirection('down');
        return { row: bestRow, col: bestCol };
      }
    }
    else {
      let currNum = gridData[row][col].downClue;
      let currCol = col;
      let bestNum = -1;
      let bestRow = -1;
      let bestCol = -1;
      for(let i = row; i >= 0; i--) {
        for(let j = currCol; j >= 0; j--) {
          if(gridData[i][j].downClue < currNum && gridData[i][j].downClue > bestNum && gridData[i][j].answer != '' && gridData[i][j].isDown) {
            bestNum = gridData[i][j].downClue;
            bestRow = i;
            bestCol = j;
          }
        }
        currCol = gridData[0].length - 1;
      }
      if(bestNum != -1) {
        for(let i = bestRow+1; i < gridData.length; i++) {
          if(gridData[i][bestCol].answer == '') {
            setActiveCell({ row: i-1, col: bestCol });
            return { row: i-1, col: bestCol };
          }
          bestRow = i;
        }
        setActiveCell({ row: bestRow, col: bestCol });
        return { row: bestRow, col: bestCol };
      }     
       else {
        for(let i = gridData.length - 1; i >= 0; i--) {
          for(let j = gridData[0].length - 1; j >= 0; j--) {
            if(gridData[i][j].acrossClue != 0 && gridData[i][j].answer != '') {
              setActiveCell({ row: i, col: j });
              setDirection('across');
              return { row: i, col: j };
            }
          }
        }
      }
    }
  }

  const moveToNextWordWithRowCol =  (row: number, col: number, direction: 'across' | 'down', iteration = 0) => {
    let newRow = row;
    let newCol = col;
    let foundBreak = false;
    let breakRow = -1;
    let breakCol = -1;

    if(direction === 'across') {
      newCol++;
      if(iteration == 0) {
        while(newCol < gridData[0].length && !foundBreak) {
          if(gridData[row][newCol].answer == '' ) {
            breakCol = newCol;
            breakRow = row;
            foundBreak = true;
          }
          newCol++;
        }
        if(!foundBreak && row < gridData.length - 1) {
          breakRow = row + 1;
          breakCol = 0;
        }
        else if (!foundBreak) {
          moveToNextWordWithRowCol(0, 0, 'down', iteration + 1);
          return;
        }
      }
      else {
        breakRow = 0;
        breakCol = 0;
      }

      while(breakRow < gridData.length) {
        while(breakCol < gridData[0].length - 1) {
          if(gridData[breakRow][breakCol].answer != '' && gridData[breakRow][breakCol + 1].answer != '') {
            if(!isWordFilledOut(breakRow, breakCol, 'across')) {
              setDirection('across');
              setEmptyCellInWord(breakRow, breakCol, 'across');
              setActiveClue(gridData[breakRow][breakCol].acrossClue);
              return;
            }
          }
          breakCol++;
        }
        breakCol = 0;
        breakRow++;
      }
      if(iteration == 3) {
        return;
      }
      moveToNextWordWithRowCol(0, 0, 'down', iteration + 1);
      return;
    }
    else {
      newRow = row-1;
      while(newRow >= 0 && gridData[newRow][col].answer != '' && gridData[newRow][col].isDown) {
        newRow--;
      }
      newRow++;
      newCol++;
      while(newRow < gridData.length) {
        while(newCol < gridData[0].length) {
          if(gridData[newRow][newCol].isDown && (newRow == 0 || !gridData[newRow - 1][newCol].isDown)) {
            if(!isWordFilledOut(newRow, newCol, 'down')) {
              setDirection('down');
              setEmptyCellInWord(newRow, newCol, 'down');
              setActiveClue(gridData[newRow][newCol].downClue);
              return;
            }
          }
          newCol++;
        }
        newCol = 0;
        newRow++;
      }
      if(iteration == 3) {
        return;
      }
      moveToNextWordWithRowCol(0, 0, 'across',  iteration + 1);
      return;
    }
  }; 

  const setEmptyCellInWord = (row: number, col: number, direction: 'across' | 'down') => {
    if(direction === 'across') {
     while(col < gridData[0].length) {
      if(gridData[row][col].currValue == '') {
          setActiveCell({ row, col });
          return;
      }
      col++;
      }
    }
    if(direction === 'down') {
      while(row < gridData.length) {
       if(gridData[row][col].currValue == '') {
           setActiveCell({ row, col });
           return;
       }
       row++;
       }
     }
  }
  
  const moveToNextWord = () => {
    // This would require more complex logic to find the next word
    // For now, just move to the next cell
    if (activeCell) {
      moveToNextWordWithRowCol(activeCell.row, activeCell.col, direction)
    }
  };

  const findFirstWordCell = () => {
    for(let row = 0; row < gridData.length; row++) {
      for(let col = 0; col < gridData[0].length; col++) {
        if(gridData[row][col].answer != '') {
          setActiveCell({ row, col });
          setActiveClueFromCell(row, col);
          return;
        }
      }
    }
  };  


  const isWordFilledOut = (row: number, col: number, direction: 'across' | 'down') => {
    if(direction === 'across') {
      while(col < gridData[0].length) {
        if(gridData[row][col].answer == '') {
          return true;
        }
        if(gridData[row][col].currValue == '') {
          return false;
        }
        col++;
      }
      return true;
    }
    else {
      while(row < gridData.length) {
        if(gridData[row][col].answer == '') {
          return true;
        }
        if(gridData[row][col].currValue == '') {
          return false;
        }
        row++;
      }
      return true;
    }
  }
  const moveToNextCell = (row: number, col: number) => {
    if (direction === 'across') {
      if(col < gridData[0].length - 1 && gridData[row][col + 1].answer != '') {
        if(gridData[row][col + 1].currValue == '') {
          setActiveCell({ row, col: col + 1 });
        }
        else {
          moveToNextCell(row, col+1);
        }
      }
      else {
        moveToNextWordWithRowCol(row, col, 'across');
      }
    } else {
      if(row < gridData.length - 1 && gridData[row + 1][col].answer != '') {
        if(gridData[row + 1][col].currValue == '') {
          setActiveCell({ row: row + 1, col });
        }
        else {
          moveToNextCell(row+1, col);
        }
      }
      else {
        moveToNextWordWithRowCol(row, col, 'down');
      }    }
  };

  const setActiveClueFromCell = (row: number, col: number) => {
    if(gridData[row][col].isAcross && gridData[row][col].isDown) {
      if(direction === 'across') {
        setActiveClue(gridData[row][col].acrossClue);
      }
      else {
        setActiveClue(gridData[row][col].downClue);
      }
    }
    else if(gridData[row][col].isAcross) {
      setDirection('across');
      setActiveClue(gridData[row][col].acrossClue);
    }
    else {
      setDirection('down');
      setActiveClue(gridData[row][col].downClue);
    }
  }

  useEffect(() => {
    // Focus the active cell
    if (activeCell) {
      const { row, col } = activeCell;
      if (
        row >= 0 &&
        row < gridData.length &&
        col >= 0 &&
        col < gridData[0].length &&
        gridData[row][col].answer != ''
      ) {
        const input = gridRefs.current[row][col];
        if (input) {
          input.focus({ preventScroll: true });
        }
      }
    }
  }, [activeCell, gridData]);

  const currentWordCells = activeCell ? getCurrentWordCells(activeCell.row, activeCell.col) : [];

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    

    const handleTouchStart = (e: TouchEvent) => {

      const target = e.target as HTMLElement;
      const cell = target.closest('.crossword-cell');
      if (cell) {
        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY };
        isScrolling.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartPos.current) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
      
      if (deltaX > 6 || deltaY > 6) {
        isScrolling.current = true;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if(!isScrolling.current) {
        e.preventDefault(); // Prevent default touch behavior
      }
      touchStartPos.current = { x: 0, y: 0 };
      isScrolling.current = false;
    };

    grid.addEventListener('touchstart', handleTouchStart, { passive: false });
    grid.addEventListener('touchmove', handleTouchMove, { passive: true });
    grid.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      grid.removeEventListener('touchstart', handleTouchStart);
      grid.removeEventListener('touchmove', handleTouchMove);
      grid.removeEventListener('touchend', handleTouchEnd);
    };
  }, [gridData]);

  useImperativeHandle(ref, () => ({
    handleKeyDown
  }));

  return (
    <div 
      ref={gridRef}
      className="crossword-grid" 
      style={{ gridTemplateColumns: `repeat(${gridData[0].length}, 1fr)`, scrollBehavior: 'auto' }}
    >
      {gridData.map((row, rowIndex) => (
        row.map((cell, colIndex) => {
          const isInCurrentWord = currentWordCells.some(
            cell => cell.row === rowIndex && cell.col === colIndex
          );
          const isActiveCell = activeCell?.row === rowIndex && activeCell?.col === colIndex;

          return (
            <div
              key={`${rowIndex}-${colIndex}`}
              className={cn(
                'crossword-cell',
                cell.answer === '' ? 'empty' : '',
                isInCurrentWord ? 'current-word' : '',
                isActiveCell ? 'active' : '',
                isComplete && cell.answer !== '' ? 'completed' : '',
                cell.isRevealed ? 'revealed' : '',
                cell.isIncorrect ? 'incorrect' : ''
              )}
              onClick={(e) => {
                // Only handle click if it's not from a touch event
                if (Date.now() - touchStartTime.current > 300) {
                  handleCellInteraction(e, rowIndex, colIndex);
                }
              }}
              onTouchStart={(e) => {
                e.stopPropagation(); // Stop event bubbling
                touchStartTime.current = Date.now();
                handleCellInteraction(e, rowIndex, colIndex);
              }}
              onTouchEnd={(e) => {
                e.stopPropagation(); // Stop event bubbling
              }}
              onTouchMove={(e) => {
                e.stopPropagation(); // Stop event bubbling
              }}
              onContextMenu={(e) => cell.answer != '' && handleRightClick(e, rowIndex, colIndex)}
            >
             {cell.answer != '' && (
                <>
                  {cell.number != 0 && <span className="cell-number">{cell.number}</span>}
                  <input
                    ref={(el) => {
                      gridRefs.current[rowIndex][colIndex] = el;
                    }}
                    type="text"
                    maxLength={1}
                    value={cell.currValue}

                    onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                    className={cn("cell-input", cell.isRevealed ? "text-blue-800" : "")}
                    disabled={isComplete}
                    readOnly
                    style={{ scrollMargin: 0 }}
                  />
                </>
              )}
            </div>
          );
        })
      ))}
    </div>
  );
});

export default CrosswordGrid;
